import { ResultType } from '../types/enums';
import getUrlQuery from './getUrlQuery';
import getQueryParams from './getQueryParams';
import getTrafficSource from './getTrafficSource';
import type { SiteConfig } from '../types/config';
import type { Step, Option, StepResult } from '../types/types';
import getAliasParams from '@de/utils/getAliasParams';

type Result = StepResult;

export const ACTION_OUTCOME = {
  QUESTION: 'dexp-question-flow',
  RESULT: 'dexp-result',
};

const warnedResolveBaseUrlMismatch: Record<string, true> = {};

const DISCOVER_HEN_PREFIX = '/discover/hen';

/**
 * Normalize a host-ish value to a tenant slug: lowercased, no leading `www.`.
 * Tenant slugs (hen-site's `[tenant]` route segment) never carry `www.`, while
 * tenant-config's canonical `tenant.domain` and the browser hostname often do.
 */
const stripWww = (value: string): string =>
  value.toLowerCase().replace(/^www\./, '');

/**
 * Normalizes a `discover`-sentinel path so ejects land on the hosted
 * `/discover/hen` route (EDUN-14677) rather than the legacy `/discover`
 * path #2996 no longer pins to hen-site at the edge.
 *
 * Leaves untouched:
 * - Paths already under `/discover/hen` (idempotent — safe to call twice).
 * - `/discover/v2` — legacy Voyager 2.0 DE, a separate product that is
 *   intentionally not part of this migration and still lives on legacy.
 * - Anything that isn't a `/discover` path at all (returned as-is).
 */
/**
 * Restricts `contextParams` to allowlisted `ctx_*` keys with non-empty
 * string values before spreading onto the result URL, so a caller can't
 * override reserved params (e.g. `actionOutcome`, `trafficSource`,
 * `formViewedCorrelationId`) via colliding keys.
 */
function filterContextParams(
  contextParams: Record<string, string> | undefined,
): Record<string, string> {
  if (!contextParams) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(contextParams).filter(
      ([key, value]) =>
        key.startsWith('ctx_') && typeof value === 'string' && value.length > 0,
    ),
  );
}

function normalizeDiscoverPath(path: string): string {
  const match = path.match(/^\/discover((?:\/[^?#]*)?)((?:[?#].*)?)$/i);
  if (!match) return path;
  const [, subPath = '', suffix = ''] = match;
  const alreadyHandled = /^\/(hen|v2)(\/|$)/i.test(subPath);
  if (alreadyHandled) return path;
  return `${DISCOVER_HEN_PREFIX}${subPath}${suffix}`;
}

/** Test helper -- resets the warn-once latch so spec runs are deterministic. */
export function __resetResolveBaseUrlWarnLatch(): void {
  for (const key of Object.keys(warnedResolveBaseUrlMismatch)) {
    delete warnedResolveBaseUrlMismatch[key];
  }
}

/**
 * Resolves the URL we should hang the result/eject path off when no
 * absolute redirect was authored.
 *
 * Production O&O: the tenant slug IS the host (e.g. `psychology.org`), so
 * the canonical `https://${subdomain}${domain}` base is correct.
 *
 * Path-prefixed staging / local dev: WP Engine preview hosts
 * (`hios....wpenginepowered.com/psychology.org/...`), localhost, ngrok,
 * Vercel preview channels, etc. put the tenant slug under the PATH
 * (mirroring the App Router `[tenant]` segment). Hard-coding the
 * production host there would yank the user off the staging session onto
 * the live site — instead we stay on the current origin and mirror the
 * URL's actual `/<segment>` prefix so an eject from
 * `…/psychology.org/online-degrees/health-psychology/` lands on
 * `…/psychology.org/discover/hen/?...`.
 *
 * The route segment is derived from `window.location.pathname` (not
 * from `siteConfig.domain`) so hosts that thread a canonical-shaped
 * value like `www.bestcolleges.com` still preserve the current origin
 * when the App Router segment is `bestcolleges.com` (and vice versa).
 * Matching is www-tolerant — we drop a leading `www.` from each side
 * before comparing, since the route segment never carries `www.` but
 * the canonical domain frequently does.
 *
 * Non-canonical tenant subdomains: host-based tenant resolution on a
 * subdomain of the tenant apex (`dev.`, `beta.`, `staging.`, preview
 * hosts). The tenant slug is neither the hostname nor in the path, but
 * the browser is already on a host that belongs to the tenant, so we
 * stay on the current origin rather than rebuilding the canonical apex
 * (which would otherwise bounce the session onto production).
 */
function resolveBaseUrl(domain: string | undefined, subdomain: string): string {
  if (!domain) return '';
  const canonicalBase = `https://${subdomain}${domain}`;
  if (typeof window === 'undefined' || !window.location) {
    return canonicalBase;
  }
  const { pathname, origin, hostname } = window.location;

  // First path segment — what Next.js routes as `[tenant]` on a
  // path-prefixed host. Empty on the canonical root (`/`).
  const firstSegment = pathname.replace(/^\/+/, '').split('/')[0] ?? '';

  const segmentLooksLikeTenant =
    !!firstSegment && stripWww(firstSegment) === stripWww(domain);

  if (segmentLooksLikeTenant) {
    // Mirror whatever the App Router actually routed (with or without
    // `www.`). Don't substitute `siteConfig.domain` — the route segment
    // is the source of truth for path-prefixed hosts; siteConfig is
    // the source of truth only for the canonical production host.
    return `${origin}/${firstSegment}`;
  }

  // Non-canonical subdomain of the tenant (host-based tenant resolution on
  // `dev.`, `beta.`, `staging.`, or preview subdomains). The tenant slug is
  // not in the path, but the browser is already on a host that belongs to
  // this tenant. Rebuilding the canonical `https://${domain}` here would
  // bounce the user to the production apex (e.g. a `discover` eject on
  // `beta.psychology.org` landing on `psychology.org`). Stay on the current
  // origin instead. This subsumes the legacy `dev.` special case
  // (`isDevLane` in `generateResultUrl`) with a uniform rule covering every
  // tenant subdomain. Guarded by `isCanonicalHost` so the canonical
  // production host and its `www.` variant keep the canonical base — which
  // preserves the apex-eject behavior and the warn-once misconfiguration
  // surface below.
  const isCanonicalHost = stripWww(hostname) === stripWww(domain);
  const isTenantSubdomain = hostname
    .toLowerCase()
    .endsWith(`.${stripWww(domain)}`);
  if (!isCanonicalHost && isTenantSubdomain) {
    return origin;
  }

  // First segment didn't match the canonical domain. That's correct
  // for production O&O (tenant slug == hostname; first path segment is
  // some page like `/online-degrees/...`), but it's also the failure
  // mode for a misconfigured staging host where the route segment
  // doesn't match `siteConfig.domain` at all (different slug, typo,
  // missing tenant prefix). Warn-once when the current host is clearly
  // not the canonical production host so the bug surfaces in the
  // console instead of silently bouncing to prod.
  const lowerHost = hostname.toLowerCase();
  const lowerDomain = stripWww(domain);
  const looksLikeProductionHost =
    lowerHost === domain.toLowerCase() ||
    lowerHost === lowerDomain ||
    lowerHost.endsWith(`.${lowerDomain}`);
  if (!looksLikeProductionHost) {
    const key = `${lowerHost}|${domain.toLowerCase()}`;
    if (!warnedResolveBaseUrlMismatch[key]) {
      warnedResolveBaseUrlMismatch[key] = true;
      // eslint-disable-next-line no-console
      console.warn(
        `[generateResultUrl] Falling back to canonical base (${canonicalBase}) on host "${hostname}" because the pathname's first segment ("${firstSegment}") does not match \`siteConfig.domain\` ("${domain}"). If you are running on a path-prefixed staging host (WP Engine preview, localhost, ngrok, Vercel preview) the App Router should have a leading [tenant] segment that matches the configured domain (www-prefix differences are tolerated). The eject will route to the canonical host instead of staying on the current origin.`,
      );
    }
  }
  return canonicalBase;
}

/**
 * Named-parameter inputs to {@link resolveEjectUrl}. Mirrors the positional
 * `generateResultUrl` signature so the two call sites that resolve the eject
 * destination (`useHandleSubmit` for navigation, `useHandleStepForward` for the
 * entry `outboundUrl` event field) share one contract and cannot drift on
 * argument order/semantics.
 */
export interface ResolveEjectUrlParams {
  step: Step & Result;
  currentStepId: string;
  correlationId: string;
  siteConfig?: Partial<SiteConfig>;
  dcsData?: {
    degree: Option;
    category: Option;
    subject: Option;
  };
  redirectUrl?: string;
  fromStepIndex?: number;
  formViewedCorrelationId?: string;
  outboundUrl?: string;
  /** Allowlisted non-PII answers as `ctx_*` query params (Discover eject only). */
  contextParams?: Record<string, string>;
}

/**
 * Single entry point for resolving a flow's eject/result destination. Thin
 * named-parameter wrapper over {@link generateResultUrl} so both the navigation
 * path (`useHandleSubmit`) and the entry `outboundUrl` event path
 * (`useHandleStepForward`) call it identically — they pass the same inputs, so
 * the resolved URL is deterministically equal for both.
 */
export const resolveEjectUrl = ({
  step,
  currentStepId,
  correlationId,
  siteConfig,
  dcsData,
  redirectUrl,
  fromStepIndex,
  formViewedCorrelationId,
  outboundUrl,
  contextParams,
}: ResolveEjectUrlParams): {
  url: string;
  outboundUrl: string;
  actionOutcome: string;
} =>
  generateResultUrl(
    step,
    currentStepId,
    correlationId,
    siteConfig,
    dcsData,
    redirectUrl,
    fromStepIndex,
    formViewedCorrelationId,
    outboundUrl,
    contextParams,
  );

export const generateResultUrl = (
  step: Step & Result,
  currentStepId: string,
  correlationId: string,
  siteConfig?: Partial<SiteConfig>,
  dcsData?: {
    degree: Option;
    category: Option;
    subject: Option;
  },
  redirectUrl?: string,
  fromStepIndex?: number,
  formViewedCorrelationId?: string,
  outboundUrl?: string,
  contextParams?: Record<string, string>,
): { url: string; outboundUrl: string; actionOutcome: string } => {
  const resultType = step?.resultType;

  // DSL-config-based result steps (a truthy `dslConfigId` with no legacy
  // `resultType`, per the dynamic-flows XOR invariant) normally render their
  // results page in-page via setCurrentStep, so they must not produce an
  // outbound URL (otherwise the `if (url) openResultUrl(...)` path in
  // useHandleSubmit opens a spurious tab). Mirror QUALIFIED_RESULTS_PAGE /
  // CUSTOM_RESULTS_PAGE which return an empty url.
  //
  // Exception: when the entry ejects via the `discover` sentinel
  // (`redirectUrl` contains `discover`), the DSL result lives on the hosted
  // Discover route rather than in the current flow instance. Fall through to
  // the `default` branch below, which builds the same-origin
  // `/discover/hen?flowid=<flowId>&jumpto=<resultId>&degree=&category=&subject=…`
  // handoff URL so the Discover route renders this DSL result in-page.
  if (step?.dslConfigId) {
    const ejectsToDiscover =
      !!redirectUrl && redirectUrl.toLowerCase().includes('discover');
    if (!ejectsToDiscover) {
      return {
        url: '',
        outboundUrl: '',
        actionOutcome: ACTION_OUTCOME.RESULT,
      };
    }
  }

  let resultUrl = redirectUrl || '/school-search';
  const queryParams = getQueryParams(window?.location?.href || '');
  let actionOutcome = ACTION_OUTCOME.QUESTION;

  const domain = siteConfig?.domain;
  const isDevLane = window?.location?.hostname?.includes('dev.') || false;
  const subdomain = isDevLane ? 'dev.' : '';
  // Guard: when the caller didn't thread siteConfig.domain, fall back to
  // a relative base ('') so the resolved URL stays a path (browser navigates
  // relative to current origin) instead of producing `https://undefined/...`.
  // The single warn surfaces the wiring gap without crashing the click.
  const baseUrl = resolveBaseUrl(domain, subdomain);
  if (!domain) {
    // eslint-disable-next-line no-console
    console.warn(
      '[generateResultUrl] siteConfig.domain is missing; falling back to relative URLs. Thread `tenant.domain` into <DynamicExperience tenant={…} />.',
    );
  }

  switch (resultType) {
    case ResultType.STUDY_MATCH:
      actionOutcome = ACTION_OUTCOME.RESULT;
      resultUrl = getUrlQuery(siteConfig?.appExperienceUrl, {
        publisher: siteConfig?.domain,
        url: window?.location?.href || '',
        degree: dcsData?.degree?.value || 'general',
        category: dcsData?.category?.value || 'general',
        subject: dcsData?.subject?.value || 'general',
        correlationId,
        trafficSource: getTrafficSource(window?.location?.href || ''),
        ...getAliasParams(),
      });
      break;

    case ResultType.OFFERINGS:
      actionOutcome = ACTION_OUTCOME.RESULT;
      resultUrl = '';
      break;

    case ResultType.DASHBOARD:
      actionOutcome = ACTION_OUTCOME.RESULT;
      resultUrl = `${baseUrl}/${step.redirectUrl || ''}`;
      break;

    case ResultType.QUALIFIED_RESULTS_PAGE:
      resultUrl = '';
      outboundUrl = '';
      actionOutcome = ACTION_OUTCOME.RESULT;
      break;

    case ResultType.CUSTOM_RESULTS_PAGE:
      resultUrl = '';
      outboundUrl = '';
      actionOutcome = ACTION_OUTCOME.RESULT;
      break;

    default:
      // Set default actionOutcome if not provided
      actionOutcome = ACTION_OUTCOME.RESULT;

      if (redirectUrl) {
        // DSL `discover` exit-on-submit sentinel: O&O sites stay on their
        // own origin (e.g. www.psychology.org/discover/hen/...); affiliates
        // hop to www.highereducation.com/{publisherDomain}/discover/hen/...
        // so leads can be attributed back to the originating affiliate site. Any
        // non-`discover` redirectUrl keeps the host's own baseUrl.
        //
        // `redirectUrl` is CMS/DSL-authored content and still carries the
        // legacy `/discover` sentinel value — #2996 pins only `/discover/hen*`
        // to hen-site at the edge, so a discover-eject path must be rewritten
        // to `/discover/hen` here (see normalizeDiscoverPath) rather than
        // trusting the authored path verbatim.
        const isDiscoverEject = redirectUrl.toLowerCase().includes('discover');
        const isAffiliate = !!siteConfig?.affiliate;
        const useAffiliateBase = isDiscoverEject && isAffiliate;
        // Publisher segment for the affiliate path. If `siteConfig.domain`
        // was not threaded by the host, fall back to `window.location.hostname`
        // so an affiliate discover eject still attributes the lead to the
        // originating origin instead of producing `/undefined/`. If we have
        // neither (SSR with no domain), drop the segment entirely so the
        // URL is still well-formed (`https://www.highereducation.com/discover/hen/...`).
        //
        // Normalized to a slug: this segment is hen-site's `[tenant]` route
        // param, and neither source is guaranteed to be one. tenant-config
        // returns the canonical host in `tenant.domain`, which for several
        // tenants carries `www.` (`www.edsmart.org`), and the hostname fallback
        // almost always does — but `/www.edsmart.org/discover/hen` is not a
        // routable tenant.
        const affiliatePublisherSource =
          domain ||
          (typeof window !== 'undefined'
            ? window.location?.hostname
            : undefined);
        const affiliatePublisher = affiliatePublisherSource
          ? stripWww(affiliatePublisherSource)
          : undefined;
        const affiliatePathPrefix = affiliatePublisher
          ? `/${affiliatePublisher}`
          : '';
        // The discover dev lane lives at `dev.highereducation.com` (no
        // `www.`) — interpolating `subdomain` in front of `www.` would
        // produce the nonexistent `dev.www.highereducation.com`.
        const affiliateHost = isDevLane
          ? 'https://dev.highereducation.com'
          : 'https://www.highereducation.com';
        const redirectBaseUrl = useAffiliateBase
          ? `${affiliateHost}${affiliatePathPrefix}`
          : baseUrl;
        // For affiliate discover ejects, the destination host is always
        // HE.com regardless of any absolute URL the author wrote -- strip
        // off the absolute origin and keep only path+query so the resolved
        // URL stays `https://www.highereducation.com/{publisherDomain}/...`.
        // For non-affiliate or non-discover redirects, absolute URLs are
        // honored as-is (legacy behavior).
        let fullRedirectUrl: string;
        if (redirectUrl.startsWith('http')) {
          if (useAffiliateBase) {
            try {
              const parsed = new URL(redirectUrl);
              fullRedirectUrl = `${redirectBaseUrl}${normalizeDiscoverPath(parsed.pathname)}${parsed.search}`;
            } catch {
              // Malformed absolute URL. Concatenating the original
              // string onto the affiliate base would emit nonsense
              // like `https://www.highereducation.com/psy.orghttps://broken`.
              // Fall back to the affiliate base + the canonical
              // `/discover/hen/` segment so the user still lands on a
              // working page that attributes the lead to the
              // originating affiliate. We also warn so the malformed
              // authoring shows up in dev/staging consoles instead of
              // silently degrading.
              // eslint-disable-next-line no-console
              console.warn(
                `[generateResultUrl] Affiliate-discover redirectUrl could not be parsed as an absolute URL; falling back to the affiliate /discover/hen/ root. redirectUrl=${JSON.stringify(redirectUrl)}`,
              );
              fullRedirectUrl = `${redirectBaseUrl}${DISCOVER_HEN_PREFIX}/`;
            }
          } else {
            fullRedirectUrl = redirectUrl;
          }
        } else {
          // Relative redirects: normalize the discover sentinel path so
          // O&O ejects (and any non-affiliate relative discover redirect)
          // land on the actual hosted route instead of the legacy path.
          const normalizedRedirectUrl = isDiscoverEject
            ? normalizeDiscoverPath(redirectUrl)
            : redirectUrl;
          fullRedirectUrl = `${redirectBaseUrl}${normalizedRedirectUrl}`;
        }
        // Emit taxonomy slugs (never the display label) on the `/discover`
        // eject URL. The downstream consumers — slug-keyed branching
        // (`getNextStep`), the cascading `dimensions` query, and the
        // Offerings `/match` taxonomy filter — all key on slugs; forwarding
        // the label (`.text`) makes `/match` reject the request and breaks
        // slug-authored branching. Prefer the canonical `slug`; fall back to
        // the bound `value` (for these DCS taxonomy Selects `value` is normally
        // the slug, but since `value` tracks `_bind.optionValueField` and can be
        // a non-slug bound field, `slug` is preferred first), then `'general'`.
        // Mirrors the STUDY_MATCH branch, which already forwards `.value`.
        resultUrl = getUrlQuery(fullRedirectUrl, {
          degree: dcsData?.degree?.slug || dcsData?.degree?.value || 'general',
          category:
            dcsData?.category?.slug || dcsData?.category?.value || 'general',
          subject:
            dcsData?.subject?.slug || dcsData?.subject?.value || 'general',
          from: currentStepId || '',
          flowid: step?.flowId || '',
          jumpto: step?.id || '',
          correlationId,
          formViewedCorrelationId: formViewedCorrelationId || '',
          actionOutcome,
          trafficSource: getTrafficSource(window?.location?.href || ''),
          ...filterContextParams(contextParams),
          ...getAliasParams(),
          fromStepIndex: fromStepIndex || '',
        });
        outboundUrl = resultUrl;
      } else {
        resultUrl = '/school-search';
        actionOutcome = ACTION_OUTCOME.RESULT;
      }
      outboundUrl = resultUrl;
  }

  if (resultUrl && queryParams) {
    // Only append query params that aren't already in the URL to avoid duplication
    const existingParams = new URLSearchParams(resultUrl.split('?')[1] || '');
    const newParams = Object.fromEntries(
      Object.entries(queryParams).filter(([key]) => !existingParams.has(key)),
    );
    if (Object.keys(newParams).length > 0) {
      resultUrl = getUrlQuery(resultUrl, newParams);
    }
  }

  outboundUrl = outboundUrl || resultUrl;

  return {
    url: resultUrl,
    outboundUrl: outboundUrl || '',
    actionOutcome: actionOutcome,
  };
};
